/**
 * UpSurv 2.0 — making the thumbnail, on the phone, at capture (round 8.1, task 3).
 *
 * The DOM half of src/core/photoThumb.ts. The picture is decoded once with
 * `createImageBitmap` — `imageOrientation: 'from-image'` so a portrait shot
 * from a phone held upright comes out upright, the way the camera meant it —
 * drawn onto a canvas at `fitWithin` size, and encoded as a JPEG at
 * `THUMB_QUALITY`. An `OffscreenCanvas` is used where the browser has one
 * (no layout, no DOM node); a plain `<canvas>` otherwise.
 *
 * ── Decoded small, not decoded large and shrunk (final review's fix wave) ──
 * A 12 MP capture decoded at full size is a ~48 MB bitmap, on a phone that
 * at that moment also holds the picture's bytes and the Blob built from
 * them. `createImageBitmap` can scale on the way out of the decoder
 * (`resizeWidth`/`resizeHeight`), but only if it is told the target size,
 * and the target size needs the SOURCE size first — which is exactly what
 * decoding would tell us. So the size is read off the file's own header
 * (`imageDimensions`, src/core/photoThumb.ts: JPEG with its EXIF
 * orientation, PNG, WebP — the first `IMAGE_HEADER_BYTES` of the Blob, no
 * decode) and the LONG side alone is handed to the decoder at `THUMB_MAX_PX`
 * (`thumbResizeHint`). A picture whose header cannot be read gets no hint
 * and decodes at full size, as before. The `resizeWidth: 640,
 * resizeHeight: 640` shape was rejected: two sides given is a stretch, not
 * a fit, and the ratio would be wrong for every picture that is not square.
 *
 * NULL ON ANY FAILURE, and that is the design. A HEIC the browser cannot
 * decode, a canvas that refuses to encode, a thumb that comes out over the
 * office's cap — none of these is a reason to refuse the photo. The photo is
 * the deliverable; the thumb is what the shelf draws when it has one. So
 * nothing here throws to its caller, and a capture with no thumb queues no
 * thumb row at all (`SurveyStore.addPhoto`).
 */

import {
  IMAGE_HEADER_BYTES,
  MAX_THUMB_BYTES,
  THUMB_CONTENT_TYPE,
  THUMB_MAX_PX,
  THUMB_QUALITY,
  fitWithin,
  imageDimensions,
  thumbResizeHint,
} from '../../core/photoThumb';

/** The size off the picture's header, or null; a Blob that cannot even be read is null too. */
async function headerDimensions(blob: Blob): Promise<ReturnType<typeof imageDimensions>> {
  try {
    const head = await blob.slice(0, IMAGE_HEADER_BYTES).arrayBuffer();
    return imageDimensions(new Uint8Array(head));
  } catch {
    return null;
  }
}

/** Encode a decoded picture at the given size; null when this browser cannot. */
async function encode(bitmap: ImageBitmap, width: number, height: number): Promise<Blob | null> {
  if (typeof OffscreenCanvas === 'function') {
    const canvas = new OffscreenCanvas(width, height);
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.drawImage(bitmap, 0, 0, width, height);
    return canvas.convertToBlob({ type: THUMB_CONTENT_TYPE, quality: THUMB_QUALITY });
  }
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.drawImage(bitmap, 0, 0, width, height);
  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, THUMB_CONTENT_TYPE, THUMB_QUALITY);
  });
}

/**
 * The thumbnail's bytes for one picture, or null when this phone cannot make
 * one. The bytes come back as an `ArrayBuffer` because that is the value the
 * store keeps (the round 8.1 lesson: a Blob in IndexedDB is a value WebKit
 * may not give back).
 */
export async function makeThumbnail(blob: Blob): Promise<ArrayBuffer | null> {
  if (typeof createImageBitmap !== 'function') return null;
  let bitmap: ImageBitmap;
  try {
    const hint = thumbResizeHint(await headerDimensions(blob));
    bitmap = await createImageBitmap(blob, {
      imageOrientation: 'from-image',
      ...hint,
      ...(Object.keys(hint).length > 0 ? { resizeQuality: 'high' as const } : {}),
    });
  } catch {
    return null;
  }
  try {
    const { width, height } = fitWithin(bitmap.width, bitmap.height, THUMB_MAX_PX);
    if (width < 1 || height < 1) return null;
    const encoded = await encode(bitmap, width, height);
    if (!encoded || encoded.size === 0 || encoded.size > MAX_THUMB_BYTES) return null;
    return await encoded.arrayBuffer();
  } catch {
    return null;
  } finally {
    bitmap.close();
  }
}
